import { Alert, Space, Typography } from 'antd';
import type { ReactNode } from 'react';

/**
 * The shell of a client-page tool section (design PORTAL-SECTIONS-2026-10-22 §1.1): the standard tool name, ONE purpose line, the
 * header buttons (the LAST one is the section's Assign), what-is-shown controls, one load-error Alert, then the cards — results first.
 * It renders no page-level success/error Alert: success is a toast, a write error stays in the drawer that sent it.
 */
export type ToolSectionProps = {
  /** The section root, e.g. 'sleep-hub' — each section keeps its existing root testid. */
  testId: string;
  title: string;
  purpose: string;
  /** Header buttons, right-aligned; the last one is the section's Assign (ToolAssignButton). */
  actions?: ReactNode;
  /** Controls that choose WHAT is shown (period, week, behavior) — under the title row. */
  toolbar?: ReactNode;
  /** The read failed (not for write errors). */
  loadError?: string | null;
  /** The load-error Alert's testid (default `${testId}-load-error`). */
  loadErrorTestId?: string;
  loading?: boolean;
  loadingText?: string;
  children: ReactNode;
};

export const ToolSection = ({
  testId,
  title,
  purpose,
  actions,
  toolbar,
  loadError,
  loadErrorTestId,
  loading,
  loadingText,
  children,
}: ToolSectionProps) => (
  <Space
    direction="vertical"
    size="middle"
    style={{ width: '100%' }}
    data-testid={testId}
  >
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        <Typography.Text type="secondary">{purpose}</Typography.Text>
      </div>
      {actions ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'flex-end',
          }}
          data-testid="tool-section-actions"
        >
          {actions}
        </div>
      ) : null}
    </div>
    {toolbar ? (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {toolbar}
      </div>
    ) : null}
    {loadError ? (
      <Alert
        type="error"
        showIcon
        message={loadError}
        data-testid={loadErrorTestId ?? `${testId}-load-error`}
      />
    ) : null}
    {loading ? (
      <Typography.Text type="secondary">{loadingText ?? '…'}</Typography.Text>
    ) : (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {children}
      </Space>
    )}
  </Space>
);
